import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PasswordInput from './PasswordInput';

interface RegisterFormData {
  email: string;
  password: string;
  confirmPassword: string;
  acceptTerms: boolean;
}

interface RegistrationError {
  message: string;
  showLoginLink?: boolean;
}

const passwordRules = [
  { id: 'length', label: 'At least 8 characters', test: (p: string) => p.length >= 8 },
  { id: 'letter', label: 'Contains a letter', test: (p: string) => /[A-Za-z]/.test(p) },
  { id: 'number', label: 'Contains a number', test: (p: string) => /\d/.test(p) },
];

const labelStyle: React.CSSProperties = {
  display: 'block',
  color: '#cccccc',
  marginBottom: '8px',
  fontSize: '14px',
  fontWeight: 500,
};

const inputStyle = (invalid: boolean): React.CSSProperties => ({
  width: '100%',
  background: '#2a2a2a',
  border: `1px solid ${invalid ? '#f44336' : '#4a4a4a'}`,
  borderRadius: '4px',
  color: '#ffffff',
  padding: '10px 12px',
  fontSize: '14px',
  outline: 'none',
});

const errorTextStyle: React.CSSProperties = {
  marginTop: '6px',
  color: '#f44336',
  fontSize: '13px',
};

const RegisterForm: React.FC = () => {
  const {
    register,
    handleSubmit,
    watch,
    setFocus,
    formState: { errors },
  } = useForm<RegisterFormData>({
    mode: 'onTouched',
    defaultValues: { email: '', password: '', confirmPassword: '', acceptTerms: false },
  });
  const { register: authRegister } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Plan choice lives on the register page so it works from ANY entry point
  // (generic "Sign up" nav button OR a Pro CTA). Defaults from "?plan=pro"
  // (the pricing Pro CTA) but the user can switch either way here.
  const [plan, setPlan] = useState<'free' | 'pro'>(
    searchParams.get('plan') === 'pro' ? 'pro' : 'free'
  );
  const isProTrial = plan === 'pro';
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<RegistrationError | null>(null);
  const liveErrorRef = useRef<HTMLDivElement>(null);

  const passwordValue = watch('password') || '';

  useEffect(() => {
    setFocus('email');
  }, [setFocus]);

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    setServerError(null);

    try {
      const email = data.email.trim().toLowerCase();
      await authRegister({
        email,
        password: data.password,
        start_pro_trial: isProTrial,
      });
      navigate('/verify-email-sent', { state: { email } });
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'EMAIL_TAKEN') {
        setServerError({ message: detail.message, showLoginLink: true });
      } else if (typeof detail === 'string') {
        setServerError({ message: detail });
      } else if (detail && typeof detail === 'object' && detail.message) {
        setServerError({ message: detail.message });
      } else {
        setServerError({ message: 'Registration failed. Please try again.' });
      }
      // Move focus to the live error region for screen readers.
      setTimeout(() => liveErrorRef.current?.focus(), 0);
    } finally {
      setIsLoading(false);
    }
  };

  const onInvalid = () => {
    if (errors.email) setFocus('email');
    else if (errors.password) setFocus('password');
    else if (errors.confirmPassword) setFocus('confirmPassword');
    else if (errors.acceptTerms) setFocus('acceptTerms');
  };

  return (
    <div className="app-layout min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="app-brand" style={{ fontSize: '32px', marginBottom: '16px' }}>
            babylon.markets
          </div>
          <h2
            className="text-3xl font-extrabold"
            style={{ color: '#ffffff', marginBottom: '8px' }}
          >
            Create your account
          </h2>
          <p className="text-sm" style={{ color: '#888888' }}>
            Already have an account?{' '}
            <Link to="/login" className="font-medium" style={{ color: '#2962ff' }}>
              Sign in
            </Link>
          </p>
          <div style={{ marginTop: 18, display: 'flex', gap: 10 }}>
            {([
              { key: 'free', name: 'Free', meta: '$0 · forever' },
              { key: 'pro', name: 'Pro', meta: '$249/yr · 14-day trial' },
            ] as const).map((opt) => {
              const selected = plan === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setPlan(opt.key)}
                  aria-pressed={selected}
                  style={{
                    flex: 1,
                    textAlign: 'left',
                    padding: '12px 14px',
                    borderRadius: 11,
                    cursor: 'pointer',
                    background: selected ? 'rgba(41,98,255,0.12)' : 'transparent',
                    border: selected ? '1.5px solid #2962ff' : '1px solid rgba(255,255,255,0.14)',
                    transition: 'all .15s ease',
                  }}
                >
                  <div style={{ fontSize: 14, fontWeight: 700, color: selected ? '#ffffff' : '#cfcfcf' }}>
                    {opt.name}
                  </div>
                  <div style={{ fontSize: 12, color: selected ? '#9bb8ff' : '#888888', marginTop: 2 }}>
                    {opt.meta}
                  </div>
                </button>
              );
            })}
          </div>
          <p style={{ marginTop: 10, fontSize: 12.5, color: '#888888' }}>
            {isProTrial
              ? '✓ 14-day Pro trial — no card required. Stays Pro until you cancel.'
              : 'Start free, upgrade to Pro anytime.'}
          </p>
        </div>

        <div className="app-card">
          <form
            className="space-y-6"
            onSubmit={handleSubmit(onSubmit, onInvalid)}
            noValidate
          >
            <div className="space-y-4">
              <div>
                <label htmlFor="email" style={labelStyle}>
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder="you@example.com"
                  aria-invalid={errors.email ? true : undefined}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                  style={inputStyle(!!errors.email)}
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^\S+@\S+\.\S+$/,
                      message: 'Please enter a valid email address',
                    },
                  })}
                />
                {errors.email && (
                  <p id="email-error" style={errorTextStyle}>
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="password" style={labelStyle}>
                  Password
                </label>
                <PasswordInput
                  id="password"
                  autoComplete="new-password"
                  placeholder="Create a password"
                  invalid={!!errors.password}
                  aria-describedby="password-rules"
                  {...register('password', {
                    required: 'Password is required',
                    validate: (v) => {
                      if (v.length < 8) return 'At least 8 characters';
                      if (!/[A-Za-z]/.test(v)) return 'Must contain a letter';
                      if (!/\d/.test(v)) return 'Must contain a number';
                      return true;
                    },
                  })}
                />
                <ul
                  id="password-rules"
                  style={{
                    margin: '8px 0 0',
                    padding: 0,
                    listStyle: 'none',
                    fontSize: '12px',
                  }}
                >
                  {passwordRules.map((rule) => {
                    const ok = rule.test(passwordValue);
                    return (
                      <li
                        key={rule.id}
                        style={{
                          color: ok ? '#22c55e' : '#888888',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          lineHeight: 1.6,
                        }}
                      >
                        <span aria-hidden="true">{ok ? '✓' : '○'}</span>
                        {rule.label}
                      </li>
                    );
                  })}
                </ul>
                {errors.password && (
                  <p id="password-error" style={errorTextStyle}>
                    {errors.password.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" style={labelStyle}>
                  Confirm password
                </label>
                <PasswordInput
                  id="confirmPassword"
                  autoComplete="new-password"
                  placeholder="Re-enter your password"
                  invalid={!!errors.confirmPassword}
                  aria-describedby={errors.confirmPassword ? 'confirm-error' : undefined}
                  {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: (v) =>
                      v === passwordValue || 'Passwords do not match',
                  })}
                />
                {errors.confirmPassword && (
                  <p id="confirm-error" style={errorTextStyle}>
                    {errors.confirmPassword.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="acceptTerms"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    color: '#cccccc',
                    fontSize: '13px',
                    lineHeight: 1.5,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    id="acceptTerms"
                    type="checkbox"
                    aria-invalid={errors.acceptTerms ? true : undefined}
                    aria-describedby={errors.acceptTerms ? 'terms-error' : undefined}
                    style={{ marginTop: '3px', accentColor: '#2962ff' }}
                    {...register('acceptTerms', {
                      required: 'Please accept the terms to continue',
                    })}
                  />
                  <span>
                    I agree to the{' '}
                    <Link
                      to="/terms"
                      style={{ color: '#2962ff' }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link
                      to="/privacy"
                      style={{ color: '#2962ff' }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </Link>
                    .
                  </span>
                </label>
                {errors.acceptTerms && (
                  <p id="terms-error" style={errorTextStyle}>
                    {errors.acceptTerms.message}
                  </p>
                )}
              </div>
            </div>

            <div
              ref={liveErrorRef}
              role="alert"
              aria-live="polite"
              tabIndex={-1}
              style={{ outline: 'none' }}
            >
              {serverError && (
                <div
                  style={{
                    background: 'rgba(244, 67, 54, 0.08)',
                    border: '1px solid rgba(244, 67, 54, 0.4)',
                    color: '#f88',
                    padding: '10px 12px',
                    borderRadius: '4px',
                    fontSize: '13px',
                    textAlign: 'center',
                  }}
                >
                  {serverError.message}
                  {serverError.showLoginLink && (
                    <>
                      {' '}
                      <Link to="/login" style={{ color: '#2962ff', fontWeight: 500 }}>
                        Sign in
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary"
              style={{
                width: '100%',
                opacity: isLoading ? 0.5 : 1,
                cursor: isLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {isLoading ? 'Creating account...' : 'Create account'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;
